import noop from 'lodash/noop';
import type { RouteLocationNormalized, NavigationFailure } from 'vue-router';

import { usePersistingErrorStore } from './store';

// PRODUCT-26110 Phase 2B2: extracted from index.ts's `.execute()` closure so the
// route<->error synchronization (and specifically its vue-router-4 failed-navigation
// guard) is unit-testable in isolation. `app` is typed `any` to match the
// established convention in ApplicationBuilder.ts (the FinalApplication generic is
// impractical to name here); only the members used below are relied upon.
export function createRouteErrorSync(app: any) {
    const errorStore = usePersistingErrorStore(app.getPinia());
    function tick() {
        const error = errorStore.error;
        // router.currentRoute is a Ref under vue-router 4.
        const route = app.router.currentRoute.value;

        if (error && route.name !== 'error.application' && route.name !== 'error') {
            app.router.replace({
                name: 'error.application',
                query: { next: route.fullPath, force: '' },
            }).catch(noop);
        } else if (!error && route.name === 'error.application') {
            const next = route.query.next;
            app.router.replace(next && typeof next === 'string' ? next : '/').catch(noop);
        }
    }

    function handleAfterEach(
        to: RouteLocationNormalized,
        _from: RouteLocationNormalized,
        failure?: NavigationFailure | void,
    ) {
        // PRODUCT-26110 Phase 2B2: bail on failed/cancelled navigations. In vue-router 3
        // `afterEach` fired only on SUCCESSFUL navigations; in vue-router 4 it also fires
        // when a navigation is aborted, cancelled, or duplicated (a truthy `failure`).
        // Without this guard, `tick()`'s own `router.replace(error.application)` cancels a
        // prior identical in-flight replace, and that cancellation re-fires `afterEach` ->
        // `tick()` -> replace -> cancel -> forever: a chained-microtask loop that never
        // yields the main thread (froze the tab so hard DevTools/CDP couldn't attach).
        if (failure) {
            return;
        }
        // Clean up full-screen errors after routing change.
        // It should be dispatched anyway, but it doesn't look clean in Vuex log.
        if (errorStore.error && to.name !== 'error.application') {
            errorStore.showError(null);
        }
        tick();
    }

    return { tick, handleAfterEach };
}
